"use client";

import { useTranslations } from "next-intl";
import { SectionShell, EditorialTitle, GradientText, AnimatedBar, RevealOnScroll } from "./_primitives";

export default function ComparisonSection() {
    const t = useTranslations();

    const rows = [
        {
            name: "Udemy Business",
            sub: t("joinInstructor_compare_udemy_sub"),
            percent: 15,
            tone: "muted",
            mech: t("joinInstructor_compare_udemy_mech"),
            mechBold: t("joinInstructor_compare_udemy_mech_bold"),
        },
        {
            name: "Dyma",
            sub: t("joinInstructor_compare_dyma_sub"),
            percent: 30,
            tone: "muted",
            mech: t("joinInstructor_compare_dyma_mech"),
            mechBold: t("joinInstructor_compare_dyma_mech_bold"),
        },
        {
            name: "Believemy Premium",
            sub: t("joinInstructor_compare_believemy_sub"),
            percent: 50,
            tone: "highlight",
            mech: t("joinInstructor_compare_believemy_mech"),
            mechBold: t("joinInstructor_compare_believemy_mech_bold"),
        },
    ];

    return (
        <SectionShell label={t("joinInstructor_compare_marker")}>
            <RevealOnScroll>
                <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                    {t("joinInstructor_compare_eyebrow")}
                </p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.05}>
                <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl mb-10 sm:mb-14 max-w-4xl">
                    {t("joinInstructor_compare_title_part1")}
                    <br />
                    {t("joinInstructor_compare_title_part2")}{" "}
                    <GradientText>{t("joinInstructor_compare_title_emphasis")}</GradientText>
                </EditorialTitle>
            </RevealOnScroll>

            <div className="grid grid-cols-12 gap-x-6 text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 pb-4 border-b border-gray-200 dark:border-white/10">
                <div className="col-span-4">{t("joinInstructor_compare_col_platform")}</div>
                <div className="col-span-3">{t("joinInstructor_compare_col_share")}</div>
                <div className="col-span-5">{t("joinInstructor_compare_col_mech")}</div>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-white/5">
                {rows.map((r, i) => (
                    <RevealOnScroll key={r.name} delay={i * 0.1}>
                        <div
                            className={`grid grid-cols-12 gap-x-6 items-center py-8 px-2 rounded-2xl ${
                                r.tone === "highlight"
                                    ? "bg-gradient-to-r from-primary/10 to-transparent border border-primary/30 my-4 px-6"
                                    : ""
                            }`}
                        >
                            <div className="col-span-12 md:col-span-4">
                                <h3
                                    className={`text-xl sm:text-2xl md:text-3xl font-bold mb-1 ${
                                        r.tone === "highlight" ? "text-primary" : "text-gray-900 dark:text-white"
                                    }`}
                                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                                >
                                    {r.name}
                                </h3>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40">
                                    {r.sub}
                                </p>
                            </div>
                            <div className="col-span-12 md:col-span-3">
                                <div
                                    className={`text-4xl sm:text-5xl md:text-6xl font-bold leading-none mb-3 ${
                                        r.tone === "highlight" ? "text-primary" : "text-gray-400 dark:text-white/40"
                                    }`}
                                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                                >
                                    {r.percent}
                                    <span className="text-3xl ml-1">%</span>
                                </div>
                                <AnimatedBar
                                    percent={r.percent * 2}
                                    delay={0.3 + i * 0.15}
                                    from={r.tone === "highlight" ? "from-[#FF7A1A]" : "from-gray-300 dark:from-white/30"}
                                    to={r.tone === "highlight" ? "to-[#E51578]" : "to-gray-300 dark:to-white/30"}
                                />
                            </div>
                            <div className="col-span-12 md:col-span-5 mt-4 md:mt-0">
                                <p className="text-gray-600 dark:text-white/70 text-sm leading-relaxed">
                                    <span
                                        className={`font-semibold ${
                                            r.tone === "highlight" ? "text-primary" : "text-gray-900 dark:text-white"
                                        }`}
                                    >
                                        {r.mechBold}
                                    </span>{" "}
                                    {r.mech}
                                </p>
                            </div>
                        </div>
                    </RevealOnScroll>
                ))}
            </div>

            <RevealOnScroll delay={0.3}>
                <div className="mt-10 p-6 rounded-2xl border border-primary/40">
                    <p
                        className="italic text-gray-700 dark:text-white/85 leading-relaxed"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinInstructor_compare_callout_part1")}{" "}
                        <span className="text-primary font-semibold not-italic">
                            {t("joinInstructor_compare_callout_emphasis")}
                        </span>
                        {/* Le fragment porte sa propre virgule : pas d'espace
                            à injecter ici, il la détacherait du mot précédent. */}
                        {t("joinInstructor_compare_callout_part2")}
                    </p>
                </div>
            </RevealOnScroll>
        </SectionShell>
    );
}
